import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Chart as GoogleChart } from 'react-google-charts';
import GoogleMapReact from 'google-map-react';
import Chart from 'chart.js';
import $ from 'jquery';
import { getReportHistory, deleteReportFromHistory, clearReportHistory } from './StreamReportsHistoryUtils';
import { generateStreamReport, calculateStreamScore, getExportTypeName, generateInsights } from './StreamReportsUtils';
import Button from '../common/Button/Button';
import Modal from '../common/Modal/Modal';
import availableBroadcastSettings from '../../constants/availableBroadcastSettings';
import config from '../../dashboard_config.json';
import './StreamReports.css';

const { generalColors } = config.dashboard.charts.colors;

const StreamReports = ({ companyId, dashboardCompanyName, dashboardAdminFirstName, dashboardAdminLastName, email, phone, primariesDisplayedNames, screenSize }) => {
    // Reports component for session analysis
    const [reports, setReports] = useState([]);
    const { reportId } = useParams();

    // Determine locale based on timezone (Respect US/Americas format, use International English for the rest of the world)
    const effectiveLocale = (() => {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timeZone && timeZone.startsWith('America/')) return 'en-US';
            return 'en-GB'; // Forces DD/MM/YYYY for Europe, Middle East, Asia, etc.
        } catch (e) {
            return navigator.language || 'en-GB';
        }
    })();

    const getSignalLabel = (strength) => {
        if (strength === undefined || strength === null) return 'No Signal';
        const s = parseInt(strength);
        if (isNaN(s)) return 'No Signal';

        if (s >= 75) return 'Excellent';
        if (s >= 50) return 'Good';
        if (s >= 25) return 'Fair';
        if (s > 0) return 'Poor';
        return 'No Signal';
    };
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportToDownload, setReportToDownload] = useState(null);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc');
    const [downloadDropdownActive, setDownloadDropdownActive] = useState(false);

    // Filtering State
    const [filterPrimary, setFilterPrimary] = useState('all');
    const [dateFilter, setDateFilter] = useState({
        label: 'All Time',
        start: null,
        end: null,
        key: 'all'
    });
    const [pendingDateFilter, setPendingDateFilter] = useState(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [showFilters, setShowFilters] = useState(false);

    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const datePickerRef = useRef(null);
    const reportRef = useRef(null);

    // Close date picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setIsDatePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        loadReports();

        // Listen for history updates from other components (e.g. Dashboard saving a new report)
        const handleHistoryUpdate = () => loadReports();
        window.addEventListener('omnistreamHistoryUpdate', handleHistoryUpdate);

        return () => {
            window.removeEventListener('omnistreamHistoryUpdate', handleHistoryUpdate);
        };
    }, [companyId]);

    useEffect(() => {
        if (!reportId) {
            setSelectedReport(null);
        }
        setDownloadDropdownActive(false);
    }, [reports, reportId]);

    // Handle Deep Linking
    useEffect(() => {
        if (reportId && reports.length > 0) {
            const report = reports.find(r => r.id === reportId);
            if (report) {
                setSelectedReport(report);
                // Scroll to overview if needed or just let the selection happen
            }
        }
    }, [reportId, reports]);

    useEffect(() => {
        if (selectedReport && chartRef.current) {
            renderChart();
        }
        setDownloadDropdownActive(false);
    }, [selectedReport]);

    const renderUsageChart = () => {
        if (!selectedReport.providerStats || selectedReport.providerStats.length === 0) return null;

        const totalPackets = selectedReport.providerStats.reduce((sum, p) => sum + (p.deliveredPackets || 0), 0);
        if (totalPackets === 0) return null;

        const data = [["Provider", "Usage"]];
        const slices = {};

        selectedReport.providerStats.forEach((p, index) => {
            const usage = p.deliveredPackets || 0;
            const percentage = (usage / totalPackets) * 100;
            // Only show significant usage to avoid clutter
            if (percentage > 0.1) {
                // Use operatorName or providerName
                data.push([p.operatorName || p.providerName || `Provider ${index + 1}`, usage]);
                slices[data.length - 2] = { color: generalColors[(data.length - 2) % generalColors.length] };
            }
        });

        if (data.length <= 1) return null;

        const options = {
            backgroundColor: 'transparent',
            legend: 'none',
            chartArea: {
                left: '10%',
                top: '5%',
                width: '80%',
                height: '90%'
            },
            pieHole: 0.4,
            pieSliceBorderColor: "#1e1e24",
            pieSliceText: 'percentage',
            pieSliceTextStyle: { color: '#fff', fontSize: 12 },
            slices: slices
        };

        const renderCustomLegend = () => (
            <div className="bonding-custom-legend" style={{
                marginTop: '10px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px 16px',
                padding: '0 10px 10px',
                borderTop: '1px solid rgba(255,255,255,0.05)',
                paddingTop: '15px'
            }}>
                {data.slice(1).map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: '#ccc', minWidth: 0 }}>
                        <span style={{
                            display: 'inline-block',
                            width: '10px',
                            height: '10px',
                            border: 'none',
                            borderRadius: '50%',
                            backgroundColor: generalColors[i % generalColors.length],
                            marginRight: '6px',
                            flexShrink: 0
                        }} />
                        <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }} title={item[0]}>{item[0]}</span>
                    </div>
                ))}
            </div>
        );

        return (
            <div className="report-section-modern" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="section-header">
                    <h3><i className="material-icons">pie_chart</i> Bonding Distribution</h3>
                    {/* Placeholder to match height of Bitrate Chart header */}
                    <div className="chart-legend" style={{ visibility: 'hidden' }}>
                        <span className="dot" /> Placeholder
                    </div>
                </div>
                <div className="report-chart-container" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="chart-wrapper" style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <GoogleChart
                            chartType="PieChart"
                            data={data}
                            options={options}
                            width="100%"
                            height="100%"
                        />
                    </div>
                    {renderCustomLegend()}
                </div>
            </div>

        );
    };
    const loadReports = () => {
        if (!companyId) return;
        const history = getReportHistory(companyId);
        // Calculate missing scores for existing reports
        const reportsWithScores = history.map(report => {
            if (report.streamScore === undefined || report.streamScore === 'N/A') {
                return { ...report, streamScore: calculateStreamScore(report, config.dashboard.streamReports) };
            }
            return report;
        });
        setReports(reportsWithScores);
    };

    const toggleDownloadDropdown = () => setDownloadDropdownActive(!downloadDropdownActive);

    const handleDownload = (format, report = null) => {
        const targetReport = report || reportToDownload || selectedReport;
        if (!targetReport) return;

        generateStreamReport(targetReport, { format });

        setDownloadDropdownActive(false);
        $('#download-report-modal').modal('hide');
    };

    const renderChart = () => {
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const ctx = chartRef.current.getContext('2d');
        const samples = selectedReport.bitrateSamples || [];

        // Generate labels (Time X) formatted as MM:SS or HH:MM:SS
        const formatSeconds = (totalSeconds) => {
            const h = Math.floor(totalSeconds / 3600);
            const m = Math.floor((totalSeconds % 3600) / 60);
            const s = Math.floor(totalSeconds % 60);
            const parts = [];
            if (h > 0) parts.push(h.toString().padStart(2, '0'));
            parts.push(m.toString().padStart(2, '0'));
            parts.push(s.toString().padStart(2, '0'));
            return parts.join(':');
        };

        const parseDurationToSeconds = (durationStr) => {
            if (!durationStr || typeof durationStr !== 'string') return 0;
            const parts = durationStr.split(':').map(Number);
            if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
            if (parts.length === 2) return parts[0] * 60 + parts[1];
            return 0;
        };

        const totalSeconds = parseDurationToSeconds(selectedReport.duration);
        const interval = samples.length > 0 ? totalSeconds / samples.length : 1;

        const labels = samples.map((_, i) => formatSeconds(i * interval));

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets: [{
                    label: 'Bitrate (Mb/s)',
                    data: samples,
                    borderColor: '#007bff',
                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    borderWidth: 2,
                    pointRadius: 0,
                    lineTension: 0.3,
                    fill: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                            fontColor: '#666'
                        },
                        gridLines: { color: 'rgba(255, 255, 255, 0.05)' }
                    }],
                    xAxes: [{
                        ticks: {
                            fontColor: '#666',
                            maxTicksLimit: 10
                        },
                        gridLines: { display: false }
                    }]
                }
            }
        });
    };


    const formatAutoDate = (dateValue) => {
        if (!dateValue || dateValue === 'N/A') return 'N/A';
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? dateValue : date.toLocaleDateString(effectiveLocale);
    };

    const formatAutoDateTime = (dateValue) => {
        if (!dateValue || dateValue === 'N/A') return 'N/A';
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? dateValue : date.toLocaleString(effectiveLocale);
    };

    const formatDate = (dateValue) => {
        return formatAutoDate(dateValue);
    };

    const dateFilterPresets = [
        { label: 'All Time', key: 'all' },
        { label: 'Today', key: 'today' },
        { label: 'Last 60 minutes', key: '1h' },
        { label: 'Last 12 hours', key: '12h' },
        { label: 'Last 24 hours', key: '24h' },
        { label: 'Last 7 days', key: '7d' },
        { label: 'Last 30 days', key: '30d' },
        { label: 'Last 90 days', key: '90d' },
    ];

    const applyPreset = (preset) => {
        const now = Date.now();
        let start = null;
        let end = null;

        switch (preset.key) {
            case 'today':
                const d = new Date();
                start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
                break;
            case '1h': start = now - 3600000; break;
            case '12h': start = now - 12 * 3600000; break;
            case '24h': start = now - 24 * 3600000; break;
            case '7d': start = now - 7 * 86400000; break;
            case '30d': start = now - 30 * 86400000; break;
            case '90d': start = now - 90 * 86400000; break;
            default: start = null; break;
        }

        setPendingDateFilter({ ...preset, start, end });
    };

    const handleCustomDateChange = (type, val) => {
        if (!val) return;
        // Parse YYYY-MM-DD as local midnight
        const [y, m, d] = val.split('-').map(Number);
        const temp = new Date(y, m - 1, d);
        if (type === 'end') temp.setHours(23, 59, 59, 999);
        const newTime = temp.getTime();

        setPendingDateFilter(prev => ({
            ...(prev || dateFilter),
            [type]: newTime,
            label: 'Custom Date Range',
            key: 'custom'
        }));
    };

    const changeCalendarMonth = (offset) => {
        const next = new Date(calendarDate);
        next.setMonth(next.getMonth() + offset);
        setCalendarDate(next);
    };

    const generateCalendar = () => {
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

        return days;
    };

    const isDateInRange = (date) => {
        if (!date) return false;
        const current = pendingDateFilter || dateFilter;
        if (!current.start || !current.end) return false;
        const d = date.getTime();
        return d >= current.start && d <= current.end;
    };

    const onDateClick = (date) => {
        if (!date) return;

        // Block future dates
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (date.getTime() > today.getTime()) return;

        const d = date.getTime();
        const current = pendingDateFilter || dateFilter;

        if (!current.start || (current.start && current.end)) {
            setPendingDateFilter({ label: 'Custom Date Range', key: 'custom', start: d, end: null });
        } else {
            if (d < current.start) {
                // Set the current start as the end (inclusive of the whole day)
                const endOfDay = new Date(current.start);
                endOfDay.setHours(23, 59, 59, 999);
                setPendingDateFilter({ label: 'Custom Date Range', key: 'custom', start: d, end: endOfDay.getTime() });
            } else {
                // Set the clicked date as the end (inclusive of the whole day)
                const endOfDay = new Date(d);
                endOfDay.setHours(23, 59, 59, 999);
                setPendingDateFilter({ ...current, label: 'Custom Date Range', key: 'custom', end: endOfDay.getTime() });
            }
        }
    };

    const confirmApplyDateFilter = () => {
        if (pendingDateFilter) {
            setDateFilter(pendingDateFilter);
            setPendingDateFilter(null);
        }
        setIsDatePickerOpen(false);
    };

    const handleDelete = (id, e) => {
        e.stopPropagation();
        setReportToDelete(id);
        $('#delete-report-modal').modal('show');
    };

    const confirmDelete = () => {
        if (reportToDelete) {
            deleteReportFromHistory(reportToDelete, companyId);
            loadReports();
            if (selectedReport && selectedReport.id === reportToDelete) setSelectedReport(null);
            setReportToDelete(null);
        }
    };

    const filteredReports = reports.filter(report => {
        // Primary Filter
        const matchesPrimary = filterPrimary === 'all' || (report.primaryName || 'Unknown User') === filterPrimary;

        // Date Filter
        let matchesDate = true;
        if (dateFilter.start || dateFilter.end) {
            // Be flexible with where we find the date (timestamp or startTime)
            const reportDate = Number(report.timestamp || report.startTime || 0);

            if (reportDate > 0) {
                if (dateFilter.start && reportDate < dateFilter.start) matchesDate = false;
                if (dateFilter.end && reportDate > dateFilter.end) matchesDate = false;
            }
        }

        return matchesPrimary && matchesDate;
    });

    const sortedReports = [...filteredReports].sort((a, b) => {
        let valA = a[sortBy];
        let valB = b[sortBy];

        // Handle nested or special sorting
        if (sortBy === 'primaryName') {
            valA = a.primaryName || '';
            valB = b.primaryName || '';
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });

    const getScoreColor = (score) => {
        const val = parseInt(score);
        if (isNaN(val)) return '#666';
        if (val >= 90) return '#4caf50';
        if (val >= 70) return '#ff9800';
        return '#f44336';
    };

    const renderSignalBars = (label) => {
        const bars = 4;
        let active = 0;
        if (label === 'Excellent') active = 4;
        if (label === 'Good') active = 3;
        if (label === 'Fair') active = 2;
        if (label === 'Poor') active = 1;
        if (label === 'No Signal') active = 1;

        const qualityClass = label ? `signal-${label.toLowerCase().replace(' ', '-')}` : '';

        return (
            <div className={`signal-bars-graphic ${qualityClass}`}>
                {[...Array(bars)].map((_, i) => (
                    <div key={i} className={`bar bar-${i + 1} ${i < active ? 'active' : ''}`} />
                ))}
            </div>
        );
    };

    const getDisplayedValue = (id, value) => {
        const setting = availableBroadcastSettings.find(s => s.id === id);
        if (setting && setting.valuesArray) {
            const option = setting.valuesArray.find(o => o.value === value);
            return option ? option.displayedName : value;
        }
        return value;
    };

    const calculateAvgBitrate = (samples) => {
        if (!samples || samples.length === 0) return 0;
        const validSamples = samples.filter(s => !isNaN(s));
        return (validSamples.reduce((a, b) => a + b, 0) / validSamples.length).toFixed(2);
    };

    return (
        <>
            <div className="reports-page-container fade-in">
                <div className="reports-sidebar">
                    <div className="sidebar-header">
                        <div className="header-title-row">
                            <h2>Session History</h2>
                            <button
                                className={`filter-toggle-btn ${(filterPrimary !== 'all' || dateFilter.key !== 'all') ? 'has-active-filters' : ''} ${showFilters ? 'active' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                                title={reports.length > 0 ? (showFilters ? "Hide Filters" : "Show Filters") : "No logs to filter"}
                                disabled={reports.length === 0}
                            >
                                <i className="material-icons">{showFilters ? 'filter_list' : 'filter_list_off'}</i>
                            </button>
                        </div>
                        <span className="report-count">{filteredReports.length} of {reports.length} Reports</span>
                    </div>

                    <div className={`sidebar-filter-section ${showFilters ? 'expanded' : 'collapsed'}`}>
                        <div className="filter-group">
                            <label><i className="material-icons">smartphone</i> Primary Device</label>
                            <div className="select-wrapper">
                                <select
                                    value={filterPrimary}
                                    onChange={(e) => setFilterPrimary(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Primaries</option>
                                    {[...new Set(reports.map(r => r.primaryName || 'Unknown User'))].sort().map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                                <i className="material-icons select-chevron">expand_more</i>
                            </div>
                        </div>

                        <div className="filter-group">
                            <label><i className="material-icons">event</i> Date Range</label>

                            <div className="date-picker-container" ref={datePickerRef}>
                                <button
                                    className={`date-picker-trigger ${isDatePickerOpen ? 'active' : ''}`}
                                    onClick={() => {
                                        setIsDatePickerOpen(!isDatePickerOpen);
                                        if (!isDatePickerOpen) setPendingDateFilter(null);
                                    }}
                                >
                                    <i className="material-icons">calendar_today</i>
                                    <div className="trigger-text">
                                        <span className="current-label">
                                            {dateFilter.label}
                                            {dateFilter.start && (
                                                <>
                                                    <span className="label-separator"> - </span>
                                                    {formatAutoDate(dateFilter.start)}
                                                    {dateFilter.end ? ` - ${formatAutoDate(dateFilter.end)}` : ''}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <i className="material-icons chevron">expand_more</i>
                                </button>

                                {isDatePickerOpen && (
                                    <div className="date-picker-popover fade-in">
                                        <div className="picker-presets">
                                            {dateFilterPresets.map(preset => (
                                                <div
                                                    key={preset.key}
                                                    className={`preset-item ${(pendingDateFilter || dateFilter).key === preset.key ? 'active' : ''}`}
                                                    onClick={() => applyPreset(preset)}
                                                >
                                                    {preset.label}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="picker-main">
                                            <div className="picker-custom-inputs">
                                                <div className="input-box">
                                                    <label>Start Date</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="date-input-display"
                                                        placeholder={effectiveLocale === 'en-US' ? 'mm/dd/yyyy' : 'dd/mm/yyyy'}
                                                        value={(pendingDateFilter || dateFilter).start ? formatAutoDate((pendingDateFilter || dateFilter).start) : ''}
                                                    />
                                                </div>
                                                <div className="input-box">
                                                    <label>End Date</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="date-input-display"
                                                        placeholder={effectiveLocale === 'en-US' ? 'mm/dd/yyyy' : 'dd/mm/yyyy'}
                                                        value={(pendingDateFilter || dateFilter).end ? formatAutoDate((pendingDateFilter || dateFilter).end) : ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="picker-calendar">
                                                <div className="calendar-header">
                                                    <button onClick={() => changeCalendarMonth(-1)}><i className="material-icons">chevron_left</i></button>
                                                    <span>{calendarDate.toLocaleDateString(effectiveLocale, { month: 'long', year: 'numeric' })}</span>
                                                    <button onClick={() => changeCalendarMonth(1)}><i className="material-icons">chevron_right</i></button>
                                                </div>
                                                <div className="calendar-grid">
                                                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d} className="weekday">{d}</div>)}
                                                    {generateCalendar().map((date, i) => {
                                                        const isFuture = date && date.getTime() > new Date().setHours(23, 59, 59, 999);
                                                        return (
                                                            <div
                                                                key={i}
                                                                className={`calendar-day ${!date ? 'empty' : ''} ${isFuture ? 'disabled' : ''} ${date && (pendingDateFilter || dateFilter).start && date.getTime() === new Date((pendingDateFilter || dateFilter).start).setHours(0, 0, 0, 0) ? 'selected-start' : ''} ${date && (pendingDateFilter || dateFilter).end && date.getTime() === new Date((pendingDateFilter || dateFilter).end).setHours(0, 0, 0, 0) ? 'selected-end' : ''} ${isDateInRange(date) ? 'in-range' : ''}`}
                                                                onClick={() => !isFuture && onDateClick(date)}
                                                            >
                                                                {date ? date.getDate() : ''}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="picker-footer">
                                                <div className="footer-status">
                                                    <span className="selection-label">{(pendingDateFilter || dateFilter).label}</span>
                                                </div>
                                                <div className="footer-actions">
                                                    <button className="apply-btn" onClick={confirmApplyDateFilter}>
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {(filterPrimary !== 'all' || dateFilter.key !== 'all') && (
                            <button className="reset-filters-btn" onClick={() => {
                                setFilterPrimary('all');
                                setDateFilter({ label: 'All Time', start: null, end: null, key: 'all' });
                            }}>
                                <i className="material-icons">close</i> Reset Filters
                            </button>
                        )}
                    </div>


                    <div className="reports-list scrollbar-hidden">
                        {sortedReports.length > 0 ? sortedReports.map(report => (
                            <div
                                key={report.id}
                                className={`report-item-card ${selectedReport && selectedReport.id === report.id ? 'selected' : ''}`}
                                onClick={() => setSelectedReport(report)}
                            >
                                <div className="report-item-main">
                                    <div className="report-item-info">
                                        <div className="report-item-primary-line">
                                            <span className="primary-name">{report.primaryName || 'Unknown User'}</span>
                                            <span className="report-separator">-</span>
                                            <span className="report-score" style={{ color: getScoreColor(report.streamScore && typeof report.streamScore === 'object' ? report.streamScore.score : report.streamScore) }}>
                                                {report.streamScore !== undefined ? `${typeof report.streamScore === 'object' ? report.streamScore.score : report.streamScore}%` : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="report-item-meta-line">
                                            <span>{formatAutoDate(report.timestamp)}</span>
                                            <span className="report-separator">-</span>
                                            <span>{report.duration || '00:00:00'}</span>
                                        </div>
                                    </div>
                                    <div className="report-item-actions">
                                        <button
                                            className="action-icon-btn download-trigger"
                                            title="Download Report"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setReportToDownload(report);
                                                $('#download-report-modal').modal('show');
                                            }}
                                        >
                                            <i className="material-icons">download</i>
                                        </button>
                                        <button
                                            className="action-icon-btn delete-trigger"
                                            title="Delete Report"
                                            onClick={(e) => handleDelete(report.id, e)}
                                        >
                                            <i className="material-icons">delete_outline</i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="empty-history-container">
                                <div className="empty-history-content">
                                    <i className="material-icons">query_stats</i>
                                    <h3>No Reports Found</h3>
                                    <p>Your session history is empty. Start a broadcast to record your first report.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="stream-reports-footer">
                        <button
                            className="stream-clear-history-btn"
                            onClick={() => {
                                $('#clear-history-modal').modal('show');
                            }}
                            disabled={reports.length === 0}
                        >
                            <i className="material-icons">delete_sweep</i>
                            <span>Clear History</span>
                        </button>
                    </div>
                </div>



                <div className="report-viewer-content">
                    {selectedReport ? (
                        <div ref={reportRef} className="report-detail-view fade-in">
                            <div className="report-detail-header">
                                <div className="header-info">
                                    <h1>{selectedReport.primaryName || 'Unknown User'} Session</h1>
                                    <p>{formatAutoDateTime(selectedReport.timestamp)}</p>
                                </div>
                                <div className="header-actions">
                                    <div className="download-dropdown-wrapper">
                                        <button
                                            className="premium-download-btn"
                                            onClick={toggleDownloadDropdown}
                                        >
                                            <i className="material-icons">download</i>
                                            <span>Download</span>
                                        </button>
                                        {downloadDropdownActive && (
                                            <div className="download-menu fade-in">

                                                <div className="menu-item" onClick={() => handleDownload('csv')}>
                                                    <i className="material-icons">description</i>
                                                    <span>Export as CSV</span>
                                                </div>
                                                <div className="menu-item" onClick={() => handleDownload('json')}>
                                                    <i className="material-icons">code</i>
                                                    <span>Export as JSON</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="report-stats-grid">
                                <div className="stat-card score-stat">
                                    <label>Stream Score</label>
                                    <div className="value" style={{ color: getScoreColor(selectedReport.streamScore && typeof selectedReport.streamScore === 'object' ? selectedReport.streamScore.score : selectedReport.streamScore) }}>
                                        {selectedReport.streamScore !== undefined ? `${typeof selectedReport.streamScore === 'object' ? selectedReport.streamScore.score : selectedReport.streamScore}%` : 'N/A'}
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <label>Avg Bitrate</label>
                                    <div className="value">
                                        {calculateAvgBitrate(selectedReport.bitrateSamples)} <span className="unit">Mb/s</span>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <label>Target Bitrate</label>
                                    <div className="value">
                                        {selectedReport.streamSettings && selectedReport.streamSettings.maxBitrate ? parseFloat(selectedReport.streamSettings.maxBitrate).toFixed(2) : 'N/A'} <span className="unit">Mb/s</span>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <label>Duration</label>
                                    <div className="value">{selectedReport.duration || '00:00:00'}</div>
                                </div>
                                <div className="stat-card">
                                    <label>Bonding</label>
                                    <div className="value">{selectedReport.maxSecondaries || 0}</div>
                                </div>
                                <div className="stat-card">
                                    <label>Device OS</label>
                                    <div className="value">
                                        {selectedReport.licenseInfo && selectedReport.licenseInfo.platform
                                            ? selectedReport.licenseInfo.platform
                                            : 'N/A'}
                                    </div>
                                </div>
                            </div>


                            {/* Stream Configuration Section */}
                            <div className="report-section-modern">
                                <h3><i className="material-icons">tune</i> Stream Configuration</h3>
                                <div className="modern-card stream-config-data">
                                    <div className="config-grid">
                                        <div className="config-item">
                                            <label>Resolution</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('resolution', selectedReport.streamSettings.resolution) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Frame Rate</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('frameRate', selectedReport.streamSettings.frameRate) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Codec</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('codec', selectedReport.streamSettings.codec) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Camera</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('camera', selectedReport.streamSettings.camera) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Profile</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('streamType', selectedReport.streamSettings.streamType) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Mode</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('scanMode', selectedReport.streamSettings.scanMode) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Fluency</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('videoFluency', selectedReport.streamSettings.videoFluency) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Broadcast Type</label>
                                            <span>{selectedReport.streamSettings ? getDisplayedValue('broadcastType', selectedReport.streamSettings.broadcastType) : 'N/A'}</span>
                                        </div>
                                        <div className="config-item">
                                            <label>Output</label>
                                            <span>{(selectedReport.streamSettings && selectedReport.streamSettings.outputName) || 'None'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="charts-row">
                                <div className="report-section-modern bitrate-chart-section">
                                    <div className="section-header">
                                        <h3><i className="material-icons">show_chart</i> Bitrate Stability (Timeline)</h3>
                                        <div className="chart-legend">
                                            <span className="dot" /> Bitrate Samples (Session Timeline)
                                        </div>
                                    </div>
                                    <div className="report-chart-container">
                                        <div className="chart-wrapper">
                                            <canvas ref={chartRef} />
                                        </div>
                                    </div>
                                </div>


                                {/* Conditionally render Bonding Chart to allow Bitrate Chart to auto-expand */}
                                {(() => {
                                    const chart = renderUsageChart();
                                    return chart ? (
                                        <div className="bonding-chart-section-wrapper" style={{ flex: 1, minWidth: '300px', display: 'flex', flexDirection: 'column' }}>
                                            {chart}
                                        </div>
                                    ) : null;
                                })()}
                            </div>

                            {/* Exports Table Section */}
                            <div className="report-section-table">
                                <h3><i className="material-icons">send</i> Exports Destinations</h3>
                                <div className="table-wrapper scrollbar-hidden">
                                    <table className="exports-report-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Type</th>
                                                <th>Address</th>
                                                <th>Port</th>
                                                <th>Stream ID</th>
                                                <th>Passphrase</th>
                                                <th>Label</th>
                                                <th>State</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedReport.exports && selectedReport.exports.length > 0 ? selectedReport.exports.map((exp, idx) => {
                                                const labelObj = (selectedReport.exportLabels || []).find(l =>
                                                    (l.id && parseInt(l.id) === parseInt(exp.id)) ||
                                                    (l.uuid && l.uuid === exp.uuid)
                                                );
                                                const label = (labelObj && labelObj.exportLabel) || 'None';

                                                return (
                                                    <tr key={idx}>
                                                        <td>{exp.id}</td>
                                                        <td>{exp.exportType || getExportTypeName(exp.type)}</td>
                                                        <td>{exp.url || exp.host || 'N/A'}</td>
                                                        <td>{exp.port || 'N/A'}</td>
                                                        <td><code>{exp.exportStreamId || 'N/A'}</code></td>
                                                        <td>{exp.key ? '******' : 'N/A'}</td>
                                                        <td><span className="export-label-tag">{label}</span></td>
                                                        <td><span className={`status-pill ${exp.isEnabled ? 'active' : 'inactive'}`}>
                                                            {exp.isEnabled ? 'Active' : 'Inactive'}
                                                        </span></td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr><td colSpan="8" className="empty-row">No exports configured during this session.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Devices Grid - Graphical Signal */}
                            <div className="report-section-modern">
                                <h3><i className="material-icons">devices</i> Connected Link Devices</h3>
                                <div className="devices-grid-modern">
                                    {(selectedReport.deviceInfo || []).map((device, idx) => (
                                        <div key={idx} className="device-stat-card">
                                            <div className="device-header">
                                                <i className="material-icons">smartphone</i>
                                                <span className="name">{device.deviceName}</span>
                                            </div>
                                            <div className="device-body">
                                                <div className="device-row primary">
                                                    <div className="dev-field">
                                                        <label>Provider</label>
                                                        <span>{device.providerName || device.operator || 'N/A'}</span>
                                                    </div>
                                                    <div className="dev-field signal-field">
                                                        <label>Signal</label>
                                                        <div className="signal-container-graphical">
                                                            {renderSignalBars(getSignalLabel(device.signalStrength))}
                                                            <span className="signal-text">{device.signalStrength ? `${device.signalStrength}%` : 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="dev-field">
                                                        <label>Temp</label>
                                                        <span>{device.batteryTemperature ? `${device.batteryTemperature}°C` : 'N/A'}</span>
                                                    </div>
                                                </div>
                                                <div className="device-row secondary">
                                                    <div className="dev-field full-width">
                                                        <label>Battery</label>
                                                        <div className="battery-indicator">
                                                            <div className="battery-level" style={{ width: `${device.batteryLevel}%` }} />
                                                            <span>{device.batteryLevel}%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Connectivity & Alarms row */}
                            <div className="report-grid-columns">
                                {/* Failures Section Removed per user request (redundant with Logs) */}

                                <div className="report-section alarms-section">
                                    <h3><i className="material-icons">warning_amber</i> Alerts Analytics</h3>
                                    <div className="modern-card insight-card">
                                        <div className="insight-scroll-container logs-scroll-container" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                            {selectedReport.alarms && selectedReport.alarms.length > 0 ? (
                                                selectedReport.alarms.map((alarm, idx) => (
                                                    <div key={idx} className="alarm-card-modern">
                                                        <div className="alarm-icon">
                                                            <i className="material-icons">report_problem</i>
                                                        </div>
                                                        <div className="alarm-info">
                                                            <span className="name">{alarm.name || alarm.Name}</span>
                                                            <span className="desc">{alarm.message || alarm.Description}</span>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="empty-section-msg">Clean session. No active alerts.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* System Logs Section */}
                            <div className="report-section-modern">
                                <h3><i className="material-icons">dvr</i> System Logs</h3>
                                <div className="table-wrapper insight-card">
                                    {/* Fixed Header Table */}
                                    <table className="exports-report-table" style={{ marginBottom: 0, borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '150px', borderBottom: 'none' }}>Time</th>
                                                <th style={{ width: '100px', borderBottom: 'none' }}>Type</th>
                                                <th style={{ borderBottom: 'none' }}>Message</th>
                                            </tr>
                                        </thead>
                                    </table>

                                    {/* Scrollable Body Table */}
                                    <div className="insight-scroll-container logs-scroll-container" style={{ maxHeight: '400px', overflowY: 'auto', padding: 0 }}>
                                        <table className="exports-report-table" style={{ marginTop: 0 }}>
                                            <tbody>
                                                {selectedReport.logs && selectedReport.logs.length > 0 ? (
                                                    selectedReport.logs.map((log, idx) => (
                                                        <tr key={idx}>
                                                            <td style={{ width: '150px' }}>{log.time}</td>
                                                            <td style={{ width: '100px' }}>
                                                                <span className={`status-pill ${log.type === 'error' ? 'inactive' : 'active'}`} style={{
                                                                    backgroundColor: log.type === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                                                    color: log.type === 'error' ? '#f44336' : '#ccc'
                                                                }}>
                                                                    {log.type || 'INFO'}
                                                                </span>
                                                            </td>
                                                            <td>{log.message}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="3" className="empty-row">No events recorded during this session.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="report-info-row">
                                {/* Stream Location Mini-Map */}
                                <div className="report-section-modern location-section">
                                    <h3><i className="material-icons">location_on</i> Stream Location</h3>
                                    {(() => {
                                        const hasLocation = selectedReport.location && (selectedReport.location.lat !== null || selectedReport.location.latitude !== null);
                                        return (
                                            <div className={`modern-card ${hasLocation ? 'map-card-flush' : ''}`} style={{ minHeight: '280px' }}>
                                                {hasLocation ? (
                                                    <div className="mini-map-container">
                                                        <GoogleMapReact
                                                            bootstrapURLKeys={{ key: config.dashboard.apiKeys.google.googleApiKey }}
                                                            defaultCenter={{
                                                                lat: selectedReport.location.lat || selectedReport.location.latitude,
                                                                lng: selectedReport.location.lng || selectedReport.location.longitude
                                                            }}
                                                            defaultZoom={15}
                                                            options={{
                                                                styles: [
                                                                    { "elementType": "geometry", "stylers": [{ "color": "#212121" }] },
                                                                    { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
                                                                    { "elementType": "labels.text.fill", "stylers": [{ "color": "#757575" }] },
                                                                    { "elementType": "labels.text.stroke", "stylers": [{ "color": "#212121" }] },
                                                                    { "featureType": "administrative", "elementType": "geometry", "stylers": [{ "color": "#757575" }] },
                                                                    { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#000000" }] },
                                                                    { "featureType": "road", "elementType": "geometry.fill", "stylers": [{ "color": "#2c2c2c" }] }
                                                                ],
                                                                disableDefaultUI: true,
                                                                draggable: false,
                                                                zoomControl: false,
                                                                scrollwheel: false,
                                                                disableDoubleClickZoom: true
                                                            }}
                                                        >
                                                            {/* Custom Marker Component to sink google-map-react internal props and avoid console warnings */}
                                                            <div
                                                                lat={selectedReport.location.lat || selectedReport.location.latitude}
                                                                lng={selectedReport.location.lng || selectedReport.location.longitude}
                                                                style={{ position: 'absolute', transform: 'translate(-50%, -100%)' }}
                                                            >
                                                                <div className="map-marker-container">
                                                                    <i className="material-icons">location_on</i>
                                                                </div>
                                                            </div>
                                                        </GoogleMapReact>
                                                    </div>
                                                ) : (
                                                    <div className="empty-location-msg">
                                                        <i className="material-icons">location_off</i>
                                                        <span>Location data not available for this session.</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Account & License Section */}
                                <div className="report-section-modern account-section">
                                    <h3><i className="material-icons">admin_panel_settings</i> Account & License</h3>
                                    <div className="modern-card">
                                        <div className="data-row">
                                            <span className="label">Company:</span>
                                            <span className="value">{(selectedReport.accountInfo && selectedReport.accountInfo.company) || dashboardCompanyName || 'N/A'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="label">Admin:</span>
                                            <span className="value">{(selectedReport.accountInfo && selectedReport.accountInfo.adminName) || [dashboardAdminFirstName, dashboardAdminLastName].filter(Boolean).join(' ') || 'N/A'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="label">Email:</span>
                                            <span className="value">{(selectedReport.accountInfo && selectedReport.accountInfo.email) || email || 'N/A'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="label">Phone:</span>
                                            <span className="value">{(selectedReport.accountInfo && selectedReport.accountInfo.phone) || phone || 'N/A'}</span>
                                        </div>
                                        <div className="separator-lite" />
                                        <div className="data-row">
                                            <span className="label">Plan:</span>
                                            <span className="value">{(selectedReport.licenseInfo && selectedReport.licenseInfo.plan) || 'N/A'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="label">Subscription:</span>
                                            <span className="value">{(selectedReport.licenseInfo && selectedReport.licenseInfo.subscription) || 'N/A'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="label">Expiration:</span>
                                            <span className="value">{formatDate((selectedReport.licenseInfo && selectedReport.licenseInfo.expirationDate) || 'N/A')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Score Analysis / Recommendations Section */}
                            {selectedReport.streamScore && typeof selectedReport.streamScore === 'object' && (
                                <div className="report-section-modern">
                                    <h3><i className="material-icons">analytics</i> Score Analysis</h3>
                                    <div className="score-analysis-card">
                                        <div className="featured-recommendation">
                                            <div className="recommendation-content">
                                                <div className="recommendation-badge">
                                                    <i className="material-icons">auto_awesome</i>
                                                    <span>EXPERT INSIGHT</span>
                                                </div>
                                                <div className="recommendation-text-wrap">
                                                    <i className="material-icons main-icon">
                                                        {selectedReport.streamScore.score > 95 ? 'verified' : 'lightbulb'}
                                                    </i>
                                                    <div className="recommendation-text">
                                                        {(() => {
                                                            const b = selectedReport.streamScore.breakdown;
                                                            if (selectedReport.streamScore.score > 95) {
                                                                return <p className="no-penalty-msg">Excellent stream! Your configuration matched your network perfectly.</p>;
                                                            }

                                                            const maxPenalty = Math.max(b.targetAlignment, b.stability, b.criticalDrops, b.disconnects);
                                                            if (maxPenalty === b.targetAlignment) {
                                                                const targetBitrate = (selectedReport.streamSettings && selectedReport.streamSettings.maxBitrate) ? parseFloat(selectedReport.streamSettings.maxBitrate).toFixed(1) : '0';
                                                                const avgBitrate = calculateAvgBitrate(selectedReport.bitrateSamples);

                                                                // Find the closest preset from availableBroadcastSettings
                                                                const bitrateSetting = availableBroadcastSettings.find(s => s.id === 'maxBitrate');
                                                                const presets = bitrateSetting ? bitrateSetting.valuesArray.map(v => parseFloat(v.value)) : [1, 2, 3, 4, 5, 6, 8, 12, 16];

                                                                // We aim for 95% of average and find the closest preset
                                                                const targetTarget = avgBitrate * 0.95;
                                                                const suggestedBitrate = presets.reduce((prev, curr) =>
                                                                    Math.abs(curr - targetTarget) < Math.abs(prev - targetTarget) ? curr : prev
                                                                );

                                                                return <p>Your network couldn't maintain the full <b>{targetBitrate} Mb/s</b> consistently. Consider a more conservative Target Bitrate of <b>{suggestedBitrate} Mb/s</b> to improve stability in challenging areas.</p>;
                                                            } else if (maxPenalty === b.stability) {
                                                                return <p><strong>Jitter or frame loss detected.</strong> We detected high variance in your signal. Try moving to an area with better 5G/LTE coverage or check for physical interference.</p>;
                                                            } else if (maxPenalty === b.criticalDrops) {
                                                                return <p><strong>Deep bitrate drops observed.</strong> Your bitrate dropped significantly in "dead zones." If using cellular bonding, ensure all modems are active.</p>;
                                                            } else if (maxPenalty === b.disconnects) {
                                                                return <p><strong>Interrupted Connection detected.</strong> The stream experienced connection blips. Ensure you are in an area with optimal coverage.</p>;
                                                            }
                                                            return <p>General network congestion detected. Avoid streaming during peak hours for better stability.</p>;
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="analysis-breakdown">
                                            <div className="penalty-card">
                                                <div className="penalty-header">
                                                    <div className="penalty-icon-wrap">
                                                        <i className="material-icons">vibration</i>
                                                    </div>
                                                    <div className="penalty-info">
                                                        <span className="penalty-name">Target Alignment</span>
                                                        <span className="penalty-description">Consistently maintaining requested bitrate levels.</span>
                                                    </div>
                                                    <span className="penalty-value">{selectedReport.streamScore.breakdown.targetAlignment > 0 ? '-' : ''}{selectedReport.streamScore.breakdown.targetAlignment}%</span>
                                                </div>
                                                <div className="penalty-footer">
                                                    <div className="penalty-bar-bg">
                                                        <div className="penalty-bar-fg" style={{ width: `${selectedReport.streamScore.breakdown.targetAlignment}%` }} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="penalty-card">
                                                <div className="penalty-header">
                                                    <div className="penalty-icon-wrap">
                                                        <i className="material-icons">speed</i>
                                                    </div>
                                                    <div className="penalty-info">
                                                        <span className="penalty-name">Stream Stability</span>
                                                        <span className="penalty-description">Consistent frame delivery and jitter-free performance.</span>
                                                    </div>
                                                    <span className="penalty-value">{selectedReport.streamScore.breakdown.stability > 0 ? '-' : ''}{selectedReport.streamScore.breakdown.stability}%</span>
                                                </div>
                                                <div className="penalty-footer">
                                                    <div className="penalty-bar-bg">
                                                        <div className="penalty-bar-fg" style={{ width: `${selectedReport.streamScore.breakdown.stability}%` }} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="penalty-card">
                                                <div className="penalty-header">
                                                    <div className="penalty-icon-wrap">
                                                        <i className="material-icons">trending_down</i>
                                                    </div>
                                                    <div className="penalty-info">
                                                        <span className="penalty-name">Critical Bitrate Drops</span>
                                                        <span className="penalty-description">Periods where quality fell below acceptable levels.</span>
                                                    </div>
                                                    <span className="penalty-value">{selectedReport.streamScore.breakdown.criticalDrops > 0 ? '-' : ''}{selectedReport.streamScore.breakdown.criticalDrops}%</span>
                                                </div>
                                                <div className="penalty-footer">
                                                    <div className="penalty-bar-bg">
                                                        <div className="penalty-bar-fg" style={{ width: `${selectedReport.streamScore.breakdown.criticalDrops}%` }} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="penalty-card">
                                                <div className="penalty-header">
                                                    <div className="penalty-icon-wrap">
                                                        <i className="material-icons">link_off</i>
                                                    </div>
                                                    <div className="penalty-info">
                                                        <span className="penalty-name">Connection Issues</span>
                                                        <span className="penalty-description">Network interruptions and session disconnects.</span>
                                                    </div>
                                                    <span className="penalty-value">{selectedReport.streamScore.breakdown.disconnects > 0 ? '-' : ''}{selectedReport.streamScore.breakdown.disconnects}%</span>
                                                </div>
                                                <div className="penalty-footer">
                                                    <div className="penalty-bar-bg">
                                                        <div className="penalty-bar-fg" style={{ width: `${selectedReport.streamScore.breakdown.disconnects}%` }} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* Session Insights Section: Summary and Suggestions */}
                            {(() => {
                                const insights = selectedReport.insights || generateInsights(selectedReport);
                                return (
                                    <div className="insights-row">
                                        <div className="report-section-modern summary-section">
                                            <h3><i className="material-icons">description</i> Session Summary</h3>
                                            <div className="modern-card insight-card">
                                                <div className="insight-scroll-container">
                                                    <ul className="insight-list">
                                                        {insights.summary && insights.summary.length > 0 ? (
                                                            insights.summary.map((item, idx) => (
                                                                <li key={idx}>
                                                                    <div className="summary-dot" />
                                                                    <span className="summary-text">{item}</span>
                                                                </li>
                                                            ))
                                                        ) : (
                                                            <li className="empty-insight">
                                                                <i className="material-icons">info_outline</i>
                                                                No significant events recorded in the summary.
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="report-section-modern suggestions-section">
                                            <h3><i className="material-icons">auto_awesome</i> Smart Suggestions</h3>
                                            <div className="modern-card insight-card">
                                                <div className="insight-scroll-container">
                                                    {insights.suggestions && insights.suggestions.length > 0 ? (
                                                        <div className="suggestions-grid">
                                                            {insights.suggestions.map((suggestion, idx) => (
                                                                <div key={idx} className={`suggestion-item ${suggestion.type || 'info'}`}>
                                                                    <div className="suggestion-icon-wrapper">
                                                                        <i className="material-icons">{suggestion.icon || 'lightbulb'}</i>
                                                                    </div>
                                                                    <div className="suggestion-content">
                                                                        <h4 className="suggestion-title">{suggestion.title}</h4>
                                                                        <p className="suggestion-message">{suggestion.message}</p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="empty-suggestions">
                                                            <div className="success-icon-wrapper">
                                                                <i className="material-icons">check_circle</i>
                                                            </div>
                                                            <h4>Optimal Setup</h4>
                                                            <p>Everything looks great! No technical optimizations suggested for this session.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="report-empty-state">
                            <i className="material-icons">assessment</i>
                            <div className="watermark-text">Omnistream Reports</div>
                            <h2>Select a session to begin analysis</h2>
                            <p>Automatically captured stream diagnostics and performance history.</p>
                        </div>
                    )}
                </div>

                {/* Modals outside the transform container (fade-in) to fix backdrop issues */}
                <Modal
                    uniqueId="clear-history-modal"
                    headerTitle="Clear All History"
                    containerClass="premium-logout-modal modal-dialog-centered"
                    bodyClass="premium-modal-body"
                    hideCloseButton={true}
                    hideHeader={true}
                    buttons={[
                        {
                            name: 'Clear All',
                            className: 'premium-logout-btn-confirm',
                            clickHandler: () => {
                                clearReportHistory(companyId);
                                setSelectedReport(null);
                            }
                        },
                        {
                            name: 'Cancel',
                            className: 'premium-logout-btn-cancel',
                            clickHandler: () => { }
                        }
                    ]}
                >
                    <div className="logout-content">
                        <i className="material-icons" style={{ fontSize: '3rem', marginBottom: '15px', color: '#de684f' }}>delete_sweep</i>
                        <p>Are you sure you want to delete ALL session reports? This action cannot be undone.</p>
                    </div>
                </Modal>

                <Modal
                    uniqueId="download-report-modal"
                    headerTitle="Download Stream Report"
                    containerClass="premium-logout-modal modal-dialog-centered"
                    bodyClass="premium-modal-body"
                    hideCloseButton={true}
                    hideHeader={true}
                    footerClass="d-none"
                    buttons={[]}
                >
                    <div className="logout-content" style={{ marginBottom: '20px' }}>
                        <i className="material-icons" style={{ fontSize: '3rem', marginBottom: '15px', color: '#de684f' }}>download</i>
                        <p>Select your preferred format to download<br />session report.</p>
                    </div>
                    <div className="download-options-grid">

                        <button
                            type="button"
                            className="download-option-card"
                            onClick={() => handleDownload('csv')}
                            data-dismiss="modal"
                        >
                            <div className="option-icon csv">
                                <i className="material-icons">description</i>
                            </div>
                            <div className="option-info">
                                <h3>Excel (CSV)</h3>
                                <p>For spreadsheets and data analysis.</p>
                            </div>
                        </button>
                        <button
                            type="button"
                            className="download-option-card"
                            onClick={() => handleDownload('json')}
                            data-dismiss="modal"
                        >
                            <div className="option-icon json">
                                <i className="material-icons">code</i>
                            </div>
                            <div className="option-info">
                                <h3>Technical (JSON)</h3>
                                <p>For technical review and integration.</p>
                            </div>
                        </button>
                    </div>
                </Modal>

                <Modal
                    uniqueId="delete-report-modal"
                    headerTitle="Delete Report"
                    containerClass="premium-logout-modal modal-dialog-centered"
                    bodyClass="premium-modal-body"
                    hideCloseButton={true}
                    hideHeader={true}
                    buttons={[
                        {
                            name: 'Delete',
                            className: 'premium-logout-btn-confirm',
                            clickHandler: confirmDelete
                        },
                        {
                            name: 'Cancel',
                            className: 'premium-logout-btn-cancel',
                            clickHandler: () => setReportToDelete(null)
                        }
                    ]}
                >
                    <div className="logout-content">
                        <i className="material-icons" style={{ fontSize: '3rem', marginBottom: '15px', color: '#de684f' }}>delete_outline</i>
                        <p>Are you sure you want to delete this session report? This action cannot be undone.</p>
                    </div>
                </Modal>
            </div>
        </>
    );
};

StreamReports.propTypes = {
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dashboardCompanyName: PropTypes.string,
    primariesDisplayedNames: PropTypes.object,
    screenSize: PropTypes.string
};

export default StreamReports;
